import type { Webpage } from '@/types';

const DB_NAME = 'solem-cache-v1';
const DB_VERSION = 1;
const STORE_NAME = 'userData';
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

export interface UserCache {
  userId: string;
  webpages: Webpage[];
  totalCount: number;
  timestamp: number;
}

// Open/create database
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'userId' });
      }
    };
  });
}

// Get cached data for user
export async function getCache(userId: string): Promise<UserCache | null> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(userId);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const data = request.result as UserCache | undefined;
        if (!data) return resolve(null);
        // Check TTL
        if (Date.now() - data.timestamp > CACHE_TTL) return resolve(null);
        resolve(data);
      };
    });
  } catch (e) {
    console.warn('IndexedDB getCache error:', e);
    return null;
  }
}

// Set cached data for user
export async function setCache(
  userId: string,
  webpages: Webpage[],
  totalCount: number
): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const data: UserCache = {
        userId,
        webpages,
        totalCount,
        timestamp: Date.now(),
      };
      const request = store.put(data);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  } catch (e) {
    console.warn('IndexedDB setCache error:', e);
  }
}

// Clear cache for user (on sign out)
export async function clearCache(userId: string): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.delete(userId);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  } catch (e) {
    console.warn('IndexedDB clearCache error:', e);
  }
}

// Clear all cache (utility function)
export async function clearAllCache(): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.clear();
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  } catch (e) {
    console.warn('IndexedDB clearAllCache error:', e);
  }
}
